import React from 'react';
import { Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { StoreState } from '../../store/store';
import {
  Box,
  Typography,
  Paper,
  Button,
  Container,
  Alert,
} from '@mui/material';
import { Lock, AdminPanelSettings } from '@mui/icons-material';

interface AdminRouteGuardProps {
  children: React.ReactNode;
}

const AdminRouteGuard: React.FC<AdminRouteGuardProps> = ({ children }) => {
  const user = useSelector((state: StoreState) => state.user.loggedInUser);

  // If no user is logged in, redirect to login
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // If user doesn't have admin role, show access denied
  if (user.role !== 'admin') {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', py: 8 }}>
        <Container maxWidth="md">
          <Paper
            elevation={3}
            sx={{
              p: 6,
              textAlign: 'center',
              borderRadius: 3,
            }}
          >
            <Box
              sx={{
                width: 120,
                height: 120,
                borderRadius: '50%',
                bgcolor: 'error.light',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 3,
              }}
            >
              <Lock sx={{ fontSize: 60, color: 'error.main' }} />
            </Box>

            <Typography variant="h4" sx={{ fontWeight: 700, mb: 2, color: 'error.main' }}>
              Access Denied
            </Typography>

            <Typography variant="h6" sx={{ mb: 2, color: 'text.secondary' }}>
              Administrator Access Required
            </Typography>

            <Alert severity="error" sx={{ mb: 4, textAlign: 'left' }}>
              <Typography variant="body1">
                <strong>Sorry, {user.fullName}!</strong>
                <br />
                You don't have permission to access the administration panel. 
                This area is restricted to users with administrator privileges only.
              </Typography>
            </Alert>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                onClick={() => window.history.back()}
                sx={{ minWidth: 120 }}
              >
                Go Back
              </Button>
              <Button
                variant="outlined"
                href="/dashboard"
                sx={{ minWidth: 120 }}
              >
                Go to Dashboard
              </Button>
            </Box>

            <Box sx={{ mt: 4, p: 2, bgcolor: 'info.light', borderRadius: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <AdminPanelSettings color="info" />
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Need Admin Access?
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                If you believe you should have administrator access, please contact your system administrator 
                or IT support team to request the necessary permissions.
              </Typography>
            </Box>
          </Paper>
        </Container>
      </Box>
    );
  }

  // User has admin role, render the protected content
  return <>{children}</>;
};

export default AdminRouteGuard;